import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { navItems, useAuth } from '../lib/auth'
import { useManagesProjects } from '../lib/useManagesProjects'
import { AlarmInbox } from './AlarmInbox'
import { StationClock, StationDate } from './StationClock'
import { UserMenu } from './UserMenu'
import { assetPath } from '../lib/assets'

type IconName = 'overview' | 'stations' | 'explore' | 'map' | 'projects' | 'alerts'
  | 'dashboard' | 'readings' | 'sensor' | 'intake' | 'admin' | 'menu' | 'arrow' | 'calculate' | 'analysis' | 'manual'

interface WorkspaceLink {
  to: string
  label: string
  icon: IconName
  access: string
}

// Overview carries everything the old Stations page did (owner, 2026-10-22):
// /stations forwards to it, keeping its search and filters.
const MONITORING: WorkspaceLink[] = [
  { to: '/overview', label: 'Overview', icon: 'overview', access: '/stations' },
  { to: '/analysis', label: 'Data Explorer', icon: 'analysis', access: '/explore' },
  { to: '/dashboards', label: 'Dashboards', icon: 'dashboard', access: '/dashboards' },
  { to: '/map', label: 'Map', icon: 'map', access: '/map' },
  { to: '/projects', label: 'Projects', icon: 'projects', access: '/projects' },
  { to: '/alerts', label: 'Alarms', icon: 'alerts', access: '/alerts' },
]

const INSTRUMENTS: WorkspaceLink[] = [
  // Compare's way in was a tab of the Data Explorer, which now opens on the analyses.
  { to: '/sensors/compare', label: 'Compare Sensors', icon: 'explore', access: '/explore' },
  { to: '/explore', label: 'Station Readings', icon: 'readings', access: '/explore' },
  { to: '/sensors/info', label: 'Sensor Info', icon: 'sensor', access: '/explore' },
  { to: '/sensors/manual', label: 'Manual Sensors', icon: 'manual', access: '/explore' },
  { to: '/sensors/calculated', label: 'Calculated Sensors', icon: 'calculate', access: '/explore' },
]

const OPERATIONS: WorkspaceLink[] = [
  { to: '/admin/intake', label: 'Data Intake', icon: 'intake', access: '/admin/intake' },
  { to: '/admin', label: 'Administration', icon: 'admin', access: '/admin' },
]

/** Navigation is a different presentation of the existing role policy.
 *  It does not grant access: Gate and the API still enforce permissions. */
export function WorkspaceShell({ children, full = false }: { children: ReactNode; full?: boolean }) {
  const { user, signOut } = useAuth()
  const managesProjects = useManagesProjects(user?.role)
  const location = useLocation()
  const mobile = useRef<HTMLDetailsElement>(null)
  const content = useRef<HTMLElement>(null)
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try { return window.localStorage.getItem('hydro:sidebar-hidden:v1') === 'true' }
    catch { return false }
  })
  const allowed = new Set(navItems(user?.role, managesProjects).map((item) => item.to))
  const monitoring = MONITORING.filter((item) => allowed.has(item.access))
  const instruments = INSTRUMENTS.filter((item) => allowed.has(item.access))
  const operations = OPERATIONS.filter((item) => allowed.has(item.access))
  const all = [...MONITORING, ...INSTRUMENTS, ...OPERATIONS]
  const activePath = location.pathname
  const current = all.find((item) => item.to === activePath)
    ?? all.filter((item) => activePath.startsWith(item.to + '/'))
      .sort((a, b) => b.to.length - a.to.length)[0]
  const demo = import.meta.env.VITE_DEMO_MODE === 'true'

  useEffect(() => {
    if (mobile.current) mobile.current.open = false
  }, [location.pathname, location.search])

  // The page no longer scrolls, its content pane does (styles: review-tools.css).
  useEffect(() => { if (content.current) content.current.scrollTop = 0 }, [location.pathname])

  // Native details is a non-modal disclosure: focus stays in normal document
  // order. Escape closes it, with focus returned to its visible summary.
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !mobile.current?.open) return
      mobile.current.open = false
      mobile.current.querySelector('summary')?.focus()
    }
    document.addEventListener('keydown', close)
    return () => document.removeEventListener('keydown', close)
  }, [])

  const navigation = (
    <>
      <NavigationGroup title="Workspace" links={monitoring} />
      <NavigationGroup title="Instruments" links={instruments} />
      <NavigationGroup title="Operations" links={operations} />
    </>
  )

  return (
    <div className={`workspace-shell${full ? ' workspace-full' : ''}${sidebarHidden ? ' ws-sidebar-hidden' : ''}`}>
      <a className="ws-skip" href="#workspace-content" onClick={(event) => {
        event.preventDefault()
        document.getElementById('workspace-content')?.focus()
      }}>Skip to content</a>
      <aside className="ws-sidebar" aria-label="Workspace sidebar" id="workspace-sidebar">
        <Link to="/overview" className="ws-brand" aria-label="HydroDiagnostics, open Overview">
          <img src={assetPath('brand/hd-logo.png')} width={737} height={343}
               alt="HydroDiagnostics, Engineering for safe environment" />
        </Link>
        <nav className="ws-navigation" aria-label="Main navigation">{navigation}</nav>
        <div className="ws-sidebar-foot">
          <span>HydroDiagnostics LLC</span>
          <span className="ws-foot-note">Engineering Data Platform</span>
          <a href="https://hydrodiagnostics.ge" target="_blank" rel="noreferrer">
            Company Website <WorkspaceIcon name="arrow" />
          </a>
        </div>
      </aside>

      <div className="ws-frame">
        <header className="ws-utilitybar">
          <button className="ws-sidebar-toggle" type="button" aria-label={sidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
            title={sidebarHidden ? 'Show sidebar' : 'Hide sidebar'} aria-controls="workspace-sidebar" aria-expanded={!sidebarHidden}
            onClick={() => setSidebarHidden(was => {
              const next = !was
              try { window.localStorage.setItem('hydro:sidebar-hidden:v1', String(next)) } catch { /* Session state still works. */ }
              return next
            })}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16"/>
              <path d={sidebarHidden ? 'm13 9 3 3-3 3' : 'm16 9-3 3 3 3'} /></svg>
          </button>
          <details className="ws-mobile-navigation" ref={mobile}>
            <summary aria-label="Open navigation"><WorkspaceIcon name="menu" /><span>Menu</span></summary>
            <nav className="ws-mobile-panel" aria-label="Mobile navigation">
              <Link to="/overview" className="ws-mobile-brand">
                <img src={assetPath('brand/hd-logo.png')} width={737} height={343} alt="HydroDiagnostics" />
              </Link>
              {navigation}
            </nav>
          </details>
          <div className="ws-context">
            <Link to="/overview">Workspace</Link>
            <span className="ws-context-separator" aria-hidden="true">/</span>
            <span aria-current="page">{current?.label ?? 'Monitoring'}</span>
          </div>
          <div className="ws-utility-actions">
            {demo && (
              <span className="ws-demo-label" title="Synthetic readings from a 90-day demonstration archive. Changes stay in this browser. No production sensors or notification delivery are connected.">
                Demo
              </span>
            )}
            {/* The date where the search was (Tamar, 2026-10-28); Overview
                keeps its own search and its / shortcut. */}
            <StationDate />
            {user && (user.role === 'viewer' || user.role === 'admin') && <AlarmInbox />}
            <div className="ws-clock"><StationClock /></div>
            {user && <UserMenu user={user} onSignOut={() => void signOut()} />}
          </div>
        </header>

        <main ref={content} id="workspace-content" tabIndex={-1} className={`ws-main${full ? ' full' : ''}`}>
          {children}
        </main>
        {!full && (
          <footer className="ws-page-foot">
            <span>HydroDiagnostics LLC</span>
            <span>Engineering for safe environment</span>
          </footer>
        )}
      </div>
    </div>
  )
}

function NavigationGroup({ title, links }: { title: string; links: WorkspaceLink[] }) {
  if (!links.length) return null
  return (
    <div className="ws-nav-group">
      <p className="ws-nav-heading">{title}</p>
      <ul>
        {links.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} end={item.to === '/admin'} className={({ isActive }) =>
              `ws-nav-link${isActive ? ' active' : ''}`}>
              <WorkspaceIcon name={item.icon} /><span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WorkspaceIcon({ name }: { name: IconName }) {
  const shapes: Record<IconName, ReactNode> = {
    analysis: <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M7 15l3-4 3 2 4-6M7 18h10" /></>,
    dashboard: <><rect x="3" y="3" width="8" height="10" rx="1.5" /><rect x="13" y="3" width="8" height="6" rx="1.5" /><rect x="13" y="11" width="8" height="10" rx="1.5" /><rect x="3" y="15" width="8" height="6" rx="1.5" /></>,
    calculate: <><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M8 5h8v4H8zM8 13h2m4 0h2m-8 4h2m4 0h2" /></>,
    overview: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
    stations: <><path d="M5 20V4h14v16M3 20h18M9 8h1m4 0h1M9 12h1m4 0h1M10 20v-4h4v4" /></>,
    manual: <><path d="M4 20h4L19 9l-4-4L4 16zM13 7l4 4" /></>,
    explore: <><path d="M3 3v18h18M6 15l4-6 4 4 6-8" /><path d="M16 5h4v4" /></>,
    map: <><path d="m3 5 6-2 6 2 6-2v16l-6 2-6-2-6 2ZM9 3v16m6-14v16" /></>,
    projects: <><path d="M3 7V5a2 2 0 0 1 2-2h4l2 3h8a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><path d="M3 10h18" /></>,
    alerts: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /><path d="M12 7v5m0 2h.01" /></>,
    readings: <><path d="M2 12h4l3-7 5 14 3-7h5" /></>,
    sensor: <><rect x="7" y="7" width="10" height="10" rx="2" /><path d="M9 2v5m6-5v5M9 17v5m6-5v5M2 9h5m-5 6h5m10-6h5m-5 6h5" /><circle cx="12" cy="12" r="1" /></>,
    intake: <><path d="M12 3v11m-4-4 4 4 4-4M4 14v6h16v-6M8 20h8" /></>,
    admin: <><path d="M4 6h16M4 12h16M4 18h16" /><rect x="7" y="3" width="4" height="6" rx="1" fill="currentColor" stroke="none" /><rect x="14" y="9" width="4" height="6" rx="1" fill="currentColor" stroke="none" /><rect x="6" y="15" width="4" height="6" rx="1" fill="currentColor" stroke="none" /></>,
    menu: <path d="M4 6h16M4 12h16M4 18h16" />,
    arrow: <path d="M7 17 17 7M7 7h10v10" />,
  }
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.55}
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">{shapes[name]}</svg>
}
